<template>
  <VDialogV3
    id="order-positions-clipboard-import-dialog"
    position="bottom"
    :entity="order"
    title="Импорт позиций"
  >
    <template #header v-if="hasPreviewTable === false">
      <VInput
        class="import-input"
        placeholder="Ctrl + V сюда выделенный диапазон (вставьте список позиций)"
        @paste="onImport"
      />

      <div v-if="importErrorsText" class="import-errors">
        {{ importErrorsText }}
      </div>
    </template>

    <section v-if="importedRawData.length > 0" class="body">
      <table>
        <thead>
        <tr>
          <th
            v-for="(cell, index) of importedRawData[0]"
            :key="'order_select_' + index"
          >
            <VSelect
              :options="importColumnList"
              v-model="importColumnOrder[index]"
              @update:modelValue="onImportColumnOrderChange($event, index)"
            />
          </th>
        </tr>
        </thead>

        <tbody>
        <tr
          v-for="(row, rowIndex) in importedRawData"
          :key="'row_' + rowIndex"
        >
          <td
            v-for="(column, columnIndex) of row"
            :key="'row_column_' + columnIndex"
          >
            {{ column ? column : '--' }}
          </td>
        </tr>
        </tbody>
      </table>
    </section>

    <VDataGridV2
      v-if="hasPreviewTable"
      class="preview-table"
      :data="indexedPreviewTable"
      sticky
    >
      <VDataGridColumnV2
        id="number"
        content-align="left"
        label="#"
        width="95px"
      >
        <template #default="{ row }">
          <span>{{ row.index + 1 }}</span>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="buyers-code"
        label="Артикул товара"
        width="8%"
      >
        <template #default="{ row }">
          <VInput
            v-model="row.data.buyersCode"
            class="buyersCode"
            disabled
            required
            :max-length="300"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        label="Количество"
        width="8%"
      >
        <template #default="{ row }">
          <VInputNumberRus
            v-model="row.data.qty"
            class="qty"
            required
            :min="0.001"
            :max="100000000"
            :precision="3"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="filter-info"
        label="Товар"
      >
        <template #default="{ row }">
          <div class="item-filter-info">
            <template v-if="row.meta.positionIsFound">
              <div :title="'Категория - ' + row.data.product.category.name">
                Категория - <b>{{ row.data.product.category.name }}</b>
              </div>

              <div :title="'Название товара - ' + row.data.product.name">
                Название фильтра - <b>{{ row.data.product.name }}</b>
              </div>
            </template>
            <template v-else>
              <span class="warning">Товар по артикулу не найден</span>
            </template>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="action"
        width="50px"
      >
        <template #default="{ row }">
          <VButton
            class="action"
            title="Удалить"
            @click="deleteRow(row.index)"
          >
            <template #icon>
              <VIcon material="delete"/>
            </template>
          </VButton>
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>

    <template #footer>
      <VButton
        v-if="hasPreviewTable === false"
        :disabled="canApproveImport === false"
        theme="primary"
        @click="approveImport"
      >
        Применить
      </VButton>

      <div v-if="hasPreviewTable" class="button-wrapper">
        <VButton
          :disabled="hasUnresolvedPositions"
          theme="primary"
          @click="doImport"
        >
          Сохранить
        </VButton>

        <span
          v-if="hasUnresolvedPositions"
          class="warning-wrapper"
        >
          <VIcon material="warning_amber"/>
          {{ qtyUnresolvedPositions }}
          {{ $core.utils.regional.ru.pluralize(qtyUnresolvedPositions, 'позиция', 'позиции', 'позиций') }}
          не найдено
        </span>

        <VButton
          v-if="hasUnresolvedPositions"
          class="warning-clean-btn"
          @click="deleteNotFoundPositions"
        >
          Удалить не найденные позиции
        </VButton>
      </div>

      <VButton class="close" @click="close">Назад</VButton>
    </template>
  </VDialogV3>
</template>

<!--<style lang="scss" src="./OrderPositionClipboardImportDialog.scss"/>-->

<script lang="ts">

import Core                    from '@/core/Core';
import ClipboardXlsParserUtils from '@/core/utils/ClipboardXlsParserUtils';
import Comment                 from '@/core/services/Comments/Comment';

import Order            from '@/domains/Orders/Order';
import OrderPosition    from '@/domains/Orders/OrderPosition';
import ProductsService  from '@/domains/Products/ProductsService';

const IMPORT_COLUMN_LIST = [
  { id: 'buyersCode', name: 'Артикул товара' },
  { id: 'name',       name: 'Наименование' },
  { id: 'qty',        name: 'Количество' },
  { id: 'commentary', name: 'Комментарий' },
] as const;

type ImportColumnOption = { id: string; name: string };
type ImportRawRow       = string[];
type PreviewRow         = { data: any; meta: { positionIsFound: boolean } };
type IndexedPreviewRow  = PreviewRow & { index: number };

export default defineComponent({
  name: 'OrderPositionsClipboardImportDialog',

  props: {
    /** Заказ, в который импортируются позиции. Диалог сам добавляет их через addPosition. */
    order: {
      type:     Object as PropType<Order>,
      required: true,
    },
  },

  emits: ['close'],

  setup() {
    return {
      importColumnList: IMPORT_COLUMN_LIST,
    };
  },

  data() {
    return {
      importedRawData:   [] as ImportRawRow[],
      importColumnOrder: [] as Array<ImportColumnOption | null>,
      previewTable:      null as PreviewRow[] | null,
      importHelper:      null as ClipboardXlsParserUtils | null,
    };
  },

  computed: {
    /**
     * previewTable с проставленным index. VDataGridV2 не передаёт index в slot props,
     * поэтому подкладываем поле руками для рендера колонки "#" и для deleteRow.
     */
    indexedPreviewTable(): IndexedPreviewRow[] {
      if (Array.isArray(this.previewTable) === false) return [];
      return this.previewTable!.map((item, index) => ({ ...item, index }));
    },

    hasPreviewTable(): boolean {
      return this.$core.utils.isNotEmptyArray(this.previewTable);
    },

    qtyUnresolvedPositions(): number {
      if (this.hasPreviewTable === false) return 0;
      return this.previewTable!.filter(row => row.meta.positionIsFound === false).length;
    },

    hasUnresolvedPositions(): boolean {
      return this.qtyUnresolvedPositions > 0;
    },

    /**
     * Можно ли применить размеченные колонки. Минимум два назначения,
     * среди которых обязательно "Артикул" и "Количество".
     */
    canApproveImport(): boolean {
      if (this.importColumnOrder.filter(item => item !== null).length < 2) return false;

      const qtyIndex  = this.importColumnOrder.findIndex(item => item !== null && item.id === 'qty');
      const codeIndex = this.importColumnOrder.findIndex(item => item !== null && item.id === 'buyersCode');

      return qtyIndex > -1 && codeIndex > -1;
    },

    /** Подсказка пользователю — что не так / можно ли продолжать. */
    importErrorsText(): string | null {
      if (this.importedRawData.length === 0) return null;
      if (this.importColumnOrder.length < 2) {
        return 'В импортированных данных должно быть минимум 2 колонки';
      }

      const qtyIndex  = this.importColumnOrder.findIndex(item => item !== null && item.id === 'qty');
      const codeIndex = this.importColumnOrder.findIndex(item => item !== null && item.id === 'buyersCode');

      if (qtyIndex > -1 && codeIndex > -1) {
        return 'Можно импортировать данные';
      } else {
        return 'Выберите колонки с данными для количества и артикулами товаров';
      }
    },
  },

  methods: {
    /**
     * Парсит вставленный из буфера обмена диапазон в строки импорта,
     * выставляет дефолтные назначения колонок (артикул/наименование/кол-во/комментарий).
     */
    onImport(event: ClipboardEvent): void {
      if (event instanceof ClipboardEvent === false) return;

      this.importHelper    = new ClipboardXlsParserUtils(event);
      this.importedRawData = this.importHelper.rows;

      this.importColumnOrder = new Array(this.importedRawData[0].length).fill(null);

      this.importColumnOrder[1] = { id: 'buyersCode', name: 'Артикул товара' };
      this.importColumnOrder[2] = { id: 'name',       name: 'Наименование' };
      this.importColumnOrder[5] = { id: 'qty',        name: 'Количество' };

      // С комментарием можно не угадать, поэтому пусть он будет последним.
      this.importColumnOrder[this.importedRawData[0].length - 1] = {
        id:   'commentary',
        name: 'Комментарий',
      };
    },

    /**
     * Меняет назначение колонки при импорте и гарантирует, что одна и та же
     * сущность не назначена в двух колонках.
     */
    onImportColumnOrderChange(value: ImportColumnOption | null, position: number): void {
      const importColumnOrder = Core.utils.cloneDeep(this.importColumnOrder);

      if (value === null) {
        importColumnOrder[position] = null;
      } else {
        const index = importColumnOrder.findIndex(item => item !== null && item.id === value.id);
        if (index !== -1) {
          importColumnOrder[index] = null;
        }
        importColumnOrder[position] = value;
      }

      this.importColumnOrder = importColumnOrder;
    },

    /** Удалить строку превью по индексу. */
    deleteRow(index: number): void {
      if (Array.isArray(this.previewTable) === false || this.previewTable!.length === 1) return;

      const previewTable = [...this.previewTable!];
      previewTable.splice(index, 1);

      this.previewTable = previewTable;
    },

    /** Удалить из превью все строки, для которых не нашлось товара. */
    deleteNotFoundPositions(): void {
      if (Array.isArray(this.previewTable) === false) return;
      this.previewTable = this.previewTable!.filter(pos => pos.meta.positionIsFound === true);
    },

    /**
     * Принять размеченный импорт и подготовить превью.
     *
     * Шаги:
     *  1. Превращаем двумерный массив сырых строк в массив объектов, используя
     *     importColumnOrder как маппинг "позиция в строке → ключ объекта".
     *     Колонки, которые пользователь не назначил, в объект не попадают.
     *  2. Приводим qty к числу (с очисткой пробелов "1 000" → 1000). NaN → null.
     *     Артикул нормализуем: пустая строка → null.
     *  3. Сбрасываем состояние режима маппинга (importedRawData/importColumnOrder),
     *     чтобы UI переключился на превью, как только оно сформируется.
     *  4. Дёргаем бэк за товарами по списку артикулов через ProductsService.getList.
     *     Возвращается мапа "артикул → товар".
     *  5. По каждой строке формы строим PreviewRow через buildPreviewRow:
     *     обогащаем найденным товаром, ставкой НДС, комментарием,
     *     либо помечаем positionIsFound = false для подсветки в превью.
     */
    async approveImport(): Promise<void> {
      // Шаг 1: маппим сырые строки в объекты по importColumnOrder.
      let form = this.importedRawData.map(item => {
        const result: Record<string, any> = {};
        this.importColumnOrder.forEach((columnOrder, index) => {
          if (columnOrder !== null) {
            result[columnOrder.id] = item[index];
          }
        });
        return result;
      });

      // Шаг 2: нормализуем qty и buyersCode.
      form = form.map(item => {
        const qtyParsed = parseInt(String(item.qty).replaceAll(' ', ''), 10);
        item.qty        = isNaN(qtyParsed) ? null : qtyParsed;
        item.buyersCode = item?.buyersCode && item.buyersCode.length > 0 ? item.buyersCode : null;
        return item;
      });

      // Шаг 3: чистим режим маппинга — UI переключится на превью, как только оно появится.
      this.importedRawData   = [];
      this.importColumnOrder = [];

      // Шаг 4: грузим товары по артикулам.
      const { data: products } = await ProductsService.getList({
        codes: form.filter(item => item !== null).map(item => item.buyersCode),
      });

      // Шаг 5: формируем превью с обогащёнными позициями.
      this.previewTable = form.map(pos => this.buildPreviewRow(pos, products));
    },

    /**
     * Превращает строку формы в строку превью: обогащает товаром, ставкой НДС,
     * комментарием. meta.positionIsFound = false если товара по артикулу нет.
     */
    buildPreviewRow(pos: any, products: Record<string, any>): PreviewRow {
      const product = products[pos.buyersCode];
      const meta    = { positionIsFound: typeof product !== 'undefined' };

      const data: any = {
        ...pos,
        vatRate:      null,
        product:      null,
        commentaries: [],
      };

      if (typeof product !== 'undefined') {
        data.product = product;

        data.vatRate = this.$core.dictionaries.vatRates
          .find((item: any) => item.id === product.productCategory.vatRate);

        if (
          this.$core.utils.string.isString(pos.commentary)
          && this.$core.utils.string.isNotEmptyString(pos.commentary.trim())
        ) {
          const comment = Comment.fromNew();
          comment.body  = pos.commentary;
          data.commentaries.push(comment);
        }

        if (!data.name) {
          data.name = product.name;
        }
      }

      delete data.commentary;

      return { data, meta };
    },

    /**
     * Финализировать импорт.
     *
     * 1. Создаём OrderPosition'ы по строкам превью и добавляем в заказ.
     * 2. Открываем диалог дат — он сам проставит даты и валюту всем позициям заказа.
     */
    async doImport(): Promise<void> {
      this.previewTable!.forEach(row => {
        const orderPosition = OrderPosition.fromImport(this.order, row.data);
        this.order.addPosition(orderPosition);
      });

      await Core.dialogs.OrderPositionSetDeliveryDatesDialog.showAsPromise({
        order:            this.order,
        showCurrencyCode: true,
      });

      this.close();
    },

    close(): void {
      this.$emit('close');
    },
  },
});
</script>
